import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  PanelHeader,
  Button,
  Divider,
  Tabs,
  Drawer,
} from "@pushpress/shared-components";
import EventDetails from "./EventDetails";
import EventSettings from "./EventSettings";
import {
  reservationsOpenOptions,
  reservationsCloseOptions,
} from "../../Calendar.utils";
import {
  EventForm,
  parseCalendarFormToCreateEventMutationInputValue,
  parseEventFormDefaultValues,
} from "./../CalendarForm.utils";
import { useToggle } from "../../../../hooks/state";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../Appointments/AppointmentsUtils";
import {
  useCreateCalendarItemMutation,
  useGetCalendarItemForEditQuery,
} from "../CalendarForm-generated.hooks";
import { useConfirm } from "../../../../components/providers/ConfirmProvider";
import { AppContext } from "../../../../context/app";

export interface OnSuccessParams {
  uuid: string;
  inviteOnly: boolean;
}

type CreateEventProps = {
  visible: boolean;
  duplicateCalendarItemUuid?: string;
  onSuccess: ({ uuid }: OnSuccessParams) => void;
  onClose?: VoidFunction;
};

const CreateEventDrawer = (props: CreateEventProps) => {
  return (
    <Drawer visible={props.visible} anchor="right" width="420px">
      {props.visible && <CreateEvent {...props} />}
    </Drawer>
  );
};

const CreateEvent = ({
  duplicateCalendarItemUuid,
  onClose,
  onSuccess,
}: CreateEventProps) => {
  const { client } = useContext(AppContext);

  const [loadingCreateEvent, loadingCreateEventActions] = useToggle();

  const { showDiscardConfirm } = useConfirm();

  const [createCalendarItemMutation] = useCreateCalendarItemMutation();

  const { data, loading } = useGetCalendarItemForEditQuery({
    variables: {
      getCalendarItemInput: {
        uuid: duplicateCalendarItemUuid!,
        clientUuid: client.uuid,
      },
    },
    skip: !duplicateCalendarItemUuid,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    trigger,
    reset,
  } = useForm<EventForm>({
    defaultValues: {
      allDay: false,
      inviteOnly: false,
      reservationsOpen: reservationsOpenOptions[0],
      reservationsClose: reservationsCloseOptions[0],
      attendanceLimit: {
        hasAttendanceLimit: false,
        number: 25,
      },
      showOnMemberApp: true,
    },
    mode: "onTouched",
  });

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const save = async (values: EventForm) => {
    try {
      loadingCreateEventActions.on();
      const variables =
        parseCalendarFormToCreateEventMutationInputValue(values);
      const result = await createCalendarItemMutation({
        variables,
      });

      toast.success(`Event successfully created!`, defaultToastOptions);
      onSuccess({
        uuid: result.data?.createCalendarItem.uuid,
        inviteOnly: values.inviteOnly,
      });
    } catch (err) {
      toast.error(`Error creating event: ${err}`, defaultToastOptions);
    } finally {
      loadingCreateEventActions.off();
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      showDiscardConfirm({ onConfirm: onClose });
    } else {
      onClose();
    }
  };

  // populate the form
  useEffect(() => {
    if (data?.getCalendarItem) {
      reset(parseEventFormDefaultValues(data.getCalendarItem, true));
    }
  }, [reset, data]);

  if (loading) {
    return null;
  }

  return (
    <>
      <PanelHeader
        title={`${duplicateCalendarItemUuid ? "Duplicate" : "Create"} event`}
      >
        <PanelHeader.ActionMenu>
          <Button
            text="Cancel"
            buttonType="secondary"
            size="medium"
            onClick={handleCancel}
          />
          <Button
            text="Save"
            buttonType="primary"
            loading={loadingCreateEvent}
            size="medium"
            iconLocation="left"
            onClick={handleSubmit(save)}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <Divider />
      <Tabs
        tabHeaders={["Details", "Settings"]}
        selectedIndex={selectedTabIndex}
        changeTab={setSelectedTabIndex}
        onTabClick={setSelectedTabIndex}
        align="left"
        hideWithCss
        onTabFocus={setSelectedTabIndex}
      >
        <EventDetails control={control} trigger={trigger} />
        <EventSettings control={control} />
      </Tabs>
    </>
  );
};

export default CreateEventDrawer;
